import type { PipeTransform } from '@angular/core';
import { Pipe } from '@angular/core';

/**
 * IsExternalPipe
 * ---------------
 * Purpose:
 *   Detect whether a string should be considered an external URL.
 *
 * Rules:
 *   - Explicit scheme (e.g., http, https, mailto, tel, ftp, data, custom+scheme)
 *       → external (returns true).
 *   - Scheme-less starting with `//` AND the first segment looks like a host
 *       (contains a dot, a port `:`, or starts with `[` for IPv6)
 *       → external (returns true).
 *   - Everything else (relative paths like 'zomer', '/zomer', '//zomer', '///zomer')
 *       → NOT external (returns false).
 *
 * Notes:
 *   - Inputs are trimmed. Empty/null/undefined → false.
 *   - Strings like 'www.example.com' (no scheme, no leading `//`)
 *       are treated as NOT external by default (they look like content, not URLs).
 *     If you need to treat these as external, add your own pre-normalization.
 *   - This pipe does NOT perform security sanitization (e.g. 'javascript:').
 *     It only classifies; you should still sanitize/validate where appropriate.
 */
@Pipe({
	name: 'isExternal',
	pure: true,
})
export class IsExternalPipe implements PipeTransform {
	transform(value?: string | null): boolean {
		const raw = (value ?? '').trim();
		if (!raw) {
			return false;
		}

		if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) {
			return true;
		}

		if (raw.startsWith('//')) {
			const host = raw.slice(2).split('/')[0];
			return host.includes('.') || host.includes(':');
		}

		return false;
	}
}
